import { FiltersMenu } from '@components/map/controls/menus/filters/FiltersMenu';
import { useMapContext } from '@components/map/MapContext';
import {
  AutomatonSectionInfoWrapper,
  ConfirmationPopup,
  DailyFlightPlans,
  DroneControlPopup,
  EngagementPopup,
  EventFormPopup,
  ExpiredDroneInfo,
  FlightPlanHistory,
  GpsQrCodePopup,
  HistoryPopup,
  MapLayerFormPopup,
  MeasureInfoPopup,
  NeutralInfo,
  PerimeterCameraInfo,
  PoiFormPopup,
  PolygonInfo,
  ResetSituationPopup,
  RoundPopup,
  SensorInfo,
  ShareTargetOnHoloviewPopup,
  StrobeInfo,
  TargetInfo,
  ZoneFormPopup,
} from '@components/map/popup';
import FlightPlanInfoWrapper from '@components/map/popup/infos/flightplans/FlightPlanInfoWrapper';
import { StatisticPopup } from '@components/map/popup/StatisticPopup';
import { useSelectorWithMapId } from '@hooks/useSelectorWithMapId';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { closePopupByMapIdAndType, switchDrawModeByMapId, updateMenuNavigationStatus } from '@redux/maps/maps.reducer';
import { selectMapNavigationOptions } from '@redux/maps/maps.selectors';
import { getNewMenuNavigationByClosedPopupType, getPopupDataType } from '@utils/popup.utils';
import { FC } from 'react';

import {
  AutomatonSectionInfoData,
  ExpiredDroneInfoData,
  FlightPlanInfoData,
  InfoComponentProps,
  InfoDataTypeEnum,
  PerimeterCameraInfoData,
  PolygonInfoData,
  PopupComponentProps,
  PopupType,
  PopupTypeEnum,
  SensorInfoData,
  StrobeInfoData,
  TargetInfoData,
} from '@/types/popup.types';

import OngoingDroneNotificationsPopup from './OngoingDroneNotificationsPopup';

interface PopupProps {
  popup: PopupType;
}

type InfoComponentPropsMap = {
  [InfoDataTypeEnum.INFO_TARGET]: InfoComponentProps<TargetInfoData>;
  [InfoDataTypeEnum.INFO_EXPIRED_DRONE]: InfoComponentProps<ExpiredDroneInfoData>;
  [InfoDataTypeEnum.INFO_AUTOMATON_SECTION]: InfoComponentProps<AutomatonSectionInfoData>;
  [InfoDataTypeEnum.INFO_PERIMETER_CAMERA]: InfoComponentProps<PerimeterCameraInfoData>;
  [InfoDataTypeEnum.INFO_SENSOR]: InfoComponentProps<SensorInfoData>;
  [InfoDataTypeEnum.INFO_POLYGON]: InfoComponentProps<PolygonInfoData>;
  [InfoDataTypeEnum.INFO_STROBE]: InfoComponentProps<StrobeInfoData>;
  [InfoDataTypeEnum.INFO_FLIGHT_PLAN]: InfoComponentProps<FlightPlanInfoData>;
};

const infoComponents: { [K in keyof InfoComponentPropsMap]: FC<InfoComponentPropsMap[K]> } = {
  [InfoDataTypeEnum.INFO_TARGET]: TargetInfo,
  [InfoDataTypeEnum.INFO_EXPIRED_DRONE]: ExpiredDroneInfo,
  [InfoDataTypeEnum.INFO_AUTOMATON_SECTION]: AutomatonSectionInfoWrapper,
  [InfoDataTypeEnum.INFO_PERIMETER_CAMERA]: PerimeterCameraInfo,
  [InfoDataTypeEnum.INFO_SENSOR]: SensorInfo,
  [InfoDataTypeEnum.INFO_POLYGON]: PolygonInfo,
  [InfoDataTypeEnum.INFO_STROBE]: StrobeInfo,
  [InfoDataTypeEnum.INFO_FLIGHT_PLAN]: FlightPlanInfoWrapper,
};

function getInfoComponent<K extends keyof InfoComponentPropsMap>(type: K): FC<InfoComponentPropsMap[K]> {
  return infoComponents[type];
}

const InfoPopup: FC<PopupComponentProps<PopupTypeEnum.INFO>> = ({ data, ...commonProps }) => {
  const InfoComponent = getInfoComponent(data.type);
  return <InfoComponent {...commonProps} {...data} />;
};

const popupComponents: Partial<{ [K in PopupTypeEnum]: FC<PopupComponentProps<K>> }> = {
  [PopupTypeEnum.CONFIRMATION_POPUP]: ConfirmationPopup,
  [PopupTypeEnum.EVENT]: EventFormPopup,
  [PopupTypeEnum.HISTORY]: HistoryPopup,
  [PopupTypeEnum.RESET_SITUATION]: ResetSituationPopup,
  [PopupTypeEnum.MEASURE_INFO]: MeasureInfoPopup,
  [PopupTypeEnum.SHARE_HOLOVIEW]: ShareTargetOnHoloviewPopup,
  [PopupTypeEnum.GPS_QR_CODE]: GpsQrCodePopup,
  [PopupTypeEnum.ROUND]: RoundPopup,
  [PopupTypeEnum.DAILY_FLIGHT_PLAN]: DailyFlightPlans,
  [PopupTypeEnum.FLIGHT_PLAN_HISTORY]: FlightPlanHistory,
  [PopupTypeEnum.DRONE_CONTROL]: DroneControlPopup,
  [PopupTypeEnum.POI]: PoiFormPopup,
  [PopupTypeEnum.FILTERS_MENU]: FiltersMenu,
  [PopupTypeEnum.ZONE]: ZoneFormPopup,
  [PopupTypeEnum.ENGAGEMENT]: EngagementPopup,
  [PopupTypeEnum.NEUTRAL_INFO]: NeutralInfo,
  [PopupTypeEnum.MAP_LAYER]: MapLayerFormPopup,
  [PopupTypeEnum.INFO]: InfoPopup,
  [PopupTypeEnum.STATISTIC_FILTER_MENU]: StatisticPopup,
  [PopupTypeEnum.ONGOING_DRONE_NOTIFICATIONS]: OngoingDroneNotificationsPopup,
};

export default function Popup({ popup }: Readonly<PopupProps>) {
  const { mapId, mapConstraintsRef, setIsMeasureActive, setMeasurePoints } = useMapContext();
  const closePopupByType = useWithDispatch(closePopupByMapIdAndType);

  const selectedToolMenuOptions = useSelectorWithMapId(selectMapNavigationOptions);
  const dispatchSelectedOptions = useWithDispatch(updateMenuNavigationStatus);

  const dispatchSwitchDrawModeByMapId = useWithDispatch(switchDrawModeByMapId);

  const type = popup.type;
  const dataType = getPopupDataType(popup);

  const handleClose = () => closePopupByType({ mapId, type, dataType });

  const closePopupFromMapMenuByType = () => {
    handleClose();

    const newToolMenuOptions = getNewMenuNavigationByClosedPopupType(
      type as PopupTypeEnum,
      selectedToolMenuOptions ?? [],
    );
    if (JSON.stringify(selectedToolMenuOptions) !== JSON.stringify(newToolMenuOptions)) {
      dispatchSelectedOptions({ mapId, selectedOptions: newToolMenuOptions });
    }
  };

  const handleCloseMeasureInfo = () => {
    closePopupFromMapMenuByType();
    setIsMeasureActive(false);
    setMeasurePoints([]);
  };

  const handleDrawnZoneFormClose = () => {
    closePopupFromMapMenuByType();
    dispatchSwitchDrawModeByMapId({ mapId, isDrawable: false });
  };

  const Component = popupComponents[popup.type] as FC<PopupComponentProps<typeof popup.type>> | undefined;

  if (!Component) {
    return null;
  }

  const closeFunctionMap: Partial<Record<PopupTypeEnum, () => void>> = {
    [PopupTypeEnum.DAILY_FLIGHT_PLAN]: closePopupFromMapMenuByType,
    [PopupTypeEnum.POI]: closePopupFromMapMenuByType,
    [PopupTypeEnum.FILTERS_MENU]: closePopupFromMapMenuByType,
    [PopupTypeEnum.ZONE]: handleDrawnZoneFormClose,
    [PopupTypeEnum.MAP_LAYER]: closePopupFromMapMenuByType,
    [PopupTypeEnum.MEASURE_INFO]: handleCloseMeasureInfo,
    [PopupTypeEnum.STATISTIC_FILTER_MENU]: closePopupFromMapMenuByType,
    [PopupTypeEnum.ENGAGEMENT]: closePopupFromMapMenuByType,
    [PopupTypeEnum.NEUTRAL_INFO]: closePopupFromMapMenuByType,
  };

  const closeFunction = closeFunctionMap[popup.type] ?? handleClose;

  return (
    <Component
      mapConstraintsRef={mapConstraintsRef}
      top={popup.position.y}
      left={popup.position.x}
      data={popup.data}
      onClose={closeFunction}
    />
  );
}
